import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { patchAccessory } from '@services/queries/onboarding/patchAccessory';

import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import { patchAssetById } from '@services/queries/onboarding/patchAssetById';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Line,
  Confirm,
  Checkbox,
  CheckContainer,
  CheckText,
  WarningContainer,
  Description,
  CheckTextDescription,
  CheckDivider,
  TextsRowFlex,
  TextsRowFlexItem,
  TextDescription,
  ToolTipText,
  Cancel,
} from './AddUnmatchedManufacturer.styles';
import Labels from '../Labels/Labels';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';

interface IAddUnmatchedManufacturer {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  closeFlow: () => void;
  newManufacturer: string;
}

export const AddUnmatchedManufacturer = ({
  open,
  onClose,
  group,
  closeFlow,
  newManufacturer,
}: IAddUnmatchedManufacturer) => {
  const portalDiv = document.getElementById('portal');

  const params = useParams<{ id: string }>();

  const queryClient = useQueryClient();

  const { studioId, userEmail, userName } = useContext(NavigationContext);

  const [selected, setSelected] = useState<string | null>(null);
  const [openRequest, setOpenRequest] = useState<boolean>(false);

  const getTextError = () => {
    if (group.mfrError?.includes('Invalid')) {
      return 'Invalid';
    }
    if (group.mfrError?.includes('Not Found')) {
      return 'Not Found';
    }

    return 'Empty';
  };

  const accessoryMutation = useMutation(patchAccessory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      setOpenRequest(true);
    },
  });

  const patchAssetMutation = useMutation(patchAssetById, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      if (selected === 'internal') {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'inventory',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
        closeFlow();
      }
      if (selected === 'request') {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'identification_requested',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      }
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <WarningContainer>
                <BlueIcon icon="error" size={16} color="#D14343" />
              </WarningContainer>
              <Title>Couldn’t match</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              <TextsRowFlexItem>
                <TextDescription>{newManufacturer}</TextDescription>
              </TextsRowFlexItem>

              <TextsRowFlexItem>
                <Labels
                  type="error"
                  text={getTextError()}
                  toolTipId="manufacturerTooltipId"
                  toolTipContent={
                    <>
                      <ToolTipText>
                        Manufacturer is either incorrect or does not exist.
                      </ToolTipText>
                    </>
                  }
                />
              </TextsRowFlexItem>
            </TextsRowFlex>

            <Description>
              Unfortunately, with the information shared we cannot make a match.
              How would you like to proceed:
            </Description>

            <CheckContainer onClick={() => setSelected('internal')}>
              <Checkbox active={selected === 'internal'} />
              <CheckText>Add as an Internal Manufacturer</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              Internal Manufacturers are just for reference and have no data
              from us.
            </CheckTextDescription>

            <CheckDivider />

            <CheckContainer onClick={() => setSelected('request')}>
              <Checkbox active={selected === 'request'} />
              <CheckText>Request Manufacturer identification</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              Select this if that’s a broadcast Manufacturer and we should have
              it in our database.
            </CheckTextDescription>

            <Line>
              <Cancel type="button" onClick={onClose}>
                Back
              </Cancel>
              <Confirm
                type="button"
                disabled={
                  !selected ||
                  accessoryMutation.isLoading ||
                  patchAssetMutation.isLoading
                }
                onClick={() => {
                  patchAssetMutation.mutate({
                    groupId: group.id,
                    studioId,
                    mfr: newManufacturer,
                    mfrError: null,
                    fileId: params.id,
                  });
                }}
              >
                Confirm
              </Confirm>
            </Line>
          </Container>
          {openRequest && (
            <ConfirmDialog
              open={openRequest}
              onClose={() => setOpenRequest(false)}
              isLoading={false}
              onConfirm={closeFlow}
              internalManufacturer
              title="Request sent"
              hideCancel
              description="You can follow this request in the “identification requests” tab. You should see a reply from us in the coming days/weeks either requesting for additional information, or with a product suggestion."
            />
          )}
        </Overlay>,
        portalDiv,
      )
    : null;
};
